import { banzosDoPontoDaEscada, localParaMundo, mundoParaLocalDoObjeto, pontosDeAncoragem } from './ancoragem'
import { orientacaoDoMosquetao } from './mosquetao'
import { parametrosDoObjeto } from './parametros'
import type { CenaTabletop, CordaTabletop, ObjetoTabletop, ReferenciaPonto, VetorTabletop } from './tipos'

/**
 * O anel de fita de ancoragem: onde ele abraça a estrutura, de que tamanho é e
 * se está sob carga.
 *
 *  - Na escada, a fita nunca abraça o degrau: ela dá a volta no BANZO (a
 *    lateral). Uma fita presa num degrau, no topo ou no pé vai para o banzo
 *    do lado de onde ela puxa; empatado (o ponto central no eixo da escada),
 *    a primeira perna vai para o banzo esquerdo e a segunda para o direito.
 *  - O anel tem um tamanho comercial (60 cm a 2,4 m de volta): dobrado, ele
 *    alcança metade disso. Sem carga ele sobra um pouco e pende; com carga
 *    ele estica até o mosquetão.
 *  - O anel está carregado quando a peça em que ele termina (mosquetão, polia,
 *    sistema) tem uma corda tensionada (sem folga no trecho que chega nela).
 *
 * TypeScript puro: sem React e sem Three.js.
 */

/** Meia diagonal da seção do banzo (5 cm × 3,5 cm): a fita passa por fora dela. */
export const RAIO_BANZO_ESCADA_M = 0.03

/** Voltas comerciais de anel de fita costurado, em metros. */
export const COMPRIMENTOS_ANEL_DE_FITA_M = [0.6, 0.8, 1.2, 1.5, 1.8, 2.4] as const

/** Sem carga, o anel sobra entre 8% e 18% da distância até o mosquetão. */
const SOBRA_MINIMA = 0.08
const SOBRA_MAXIMA = 0.18

/** Peças que já têm olhal ou passagem: a fita termina nelas, não as abraça. */
const PECAS_SEM_ABRACO: ReadonlySet<ObjetoTabletop['tipo']> = new Set([
  'mosquetao',
  'pontoAncoragem',
  'placaAncoragem',
  'polia',
  'poliaDupla',
  'bloqueador',
  'sistemaVM',
  'descensor',
  'noOito',
  'noFiel',
  'maca',
])

export type LadoDoBanzo = 'esq' | 'dir'

/** Onde o anel abraça a estrutura. */
export interface ApoioDaFita {
  /** O centro do membro abraçado (o eixo do banzo, do tronco). */
  readonly ponto: VetorTabletop
  /** Direção do membro abraçado, unitária; `null` quando não há um (a fita converge no ponto). */
  readonly eixo: VetorTabletop | null
  /** Quanto a fita se afasta do centro ao dar a volta. */
  readonly raio: number
}

type V = VetorTabletop
const sub = (a: V, b: V): V => [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
const dist = (a: V, b: V) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2])
const unit = (a: V): V => {
  const c = Math.hypot(a[0], a[1], a[2])
  return c > 1e-12 ? [a[0] / c, a[1] / c, a[2] / c] : [0, 0, 0]
}
const esc = (a: V, k: number): V => [a[0] * k, a[1] * k, a[2] * k]
const somaL = (a: V, b: V): V => [a[0] + b[0], a[1] + b[1], a[2] + b[2]]

/** A fita abraça esta peça (escada, toco, viatura...) em vez de só clipar nela. */
export const fitaAbraca = (objeto: Pick<ObjetoTabletop, 'tipo'>): boolean => !PECAS_SEM_ABRACO.has(objeto.tipo)

/**
 * Onde o anel abraça o objeto da `referencia`, puxado para `rumo` (o ponto
 * central, o mosquetão). `fator` é a escala de destaque da peça.
 */
export function apoioDaFita(
  cena: CenaTabletop,
  referencia: ReferenciaPonto,
  rumo: V | null,
  ladoNoEmpate: LadoDoBanzo = 'esq',
  fator = 1,
  naEscada: PernaNaEscada | null = null,
): ApoioDaFita | null {
  const objeto = cena.objetos.find((o) => o.id === referencia.idObjeto)
  if (!objeto) return null
  const pontos = pontosDeAncoragem(objeto)
  const achado = pontos.find((p) => p.id === referencia.idPonto)
  if (!achado) return null
  const mundo = (local: V) => localParaMundo(esc(local, fator), objeto.transformacao)
  const direcaoNoMundo = (local: V, eixoLocal: V) => unit(sub(mundo(somaL(local, eixoLocal)), mundo(local)))

  if (objeto.tipo === 'escada') {
    const pe = pontos.find((p) => p.id === 'pe')!.local
    const topo = pontos.find((p) => p.id === 'topo')!.local
    const eixoLocal = unit(sub(topo, pe))
    // com outras pernas na mesma escada, todas ficam na mesma altura, uma em cada banzo
    if (naEscada) {
      const [esq, dir] = banzosDoPontoDaEscada(naEscada.nivelLocal)
      const local = naEscada.lado === 'esq' ? esq : dir
      return { ponto: mundo(local), eixo: direcaoNoMundo(local, eixoLocal), raio: RAIO_BANZO_ESCADA_M * fator }
    }
    const [esq, dir] = banzosDoPontoDaEscada(achado.local)
    let local: V
    if (referencia.idPonto.startsWith('banzo-esq-')) local = esq
    else if (referencia.idPonto.startsWith('banzo-dir-')) local = dir
    else {
      // degrau, topo ou pé: a fita vai para o banzo do lado para onde ela puxa
      const de = rumo ? dist(mundo(esq), rumo) : 0
      const dd = rumo ? dist(mundo(dir), rumo) : 0
      const empate = Math.abs(de - dd) < 0.02
      local = empate ? (ladoNoEmpate === 'esq' ? esq : dir) : de < dd ? esq : dir
    }
    return { ponto: mundo(local), eixo: direcaoNoMundo(local, eixoLocal), raio: RAIO_BANZO_ESCADA_M * fator }
  }

  if (objeto.tipo === 'toco') {
    const raio = (parametrosDoObjeto(objeto).diametroM ?? 0.3) / 2
    return { ponto: mundo(achado.local), eixo: direcaoNoMundo(achado.local, [0, 1, 0]), raio: raio * fator }
  }

  return { ponto: mundo(achado.local), eixo: null, raio: 0 }
}

/** Onde uma perna abraça a escada quando há outras pernas na mesma escada. */
export interface PernaNaEscada {
  /** Um ponto da escada (no referencial dela) na altura comum das pernas. */
  readonly nivelLocal: V
  readonly lado: LadoDoBanzo
}

/**
 * As pernas de uma mesma ancoragem (ou de uma mesma fita) presas na mesma
 * escada ficam na MESMA altura, uma em cada banzo: um V com uma perna acima da
 * outra puxa a escada de lado e carrega as pernas de forma desigual. A altura
 * comum é a da perna mais alta (a mais longe do pé). O banzo escolhido
 * (`banzo-esq-N`, `banzo-dir-N`) fica do lado escolhido; as demais pernas
 * se dividem entre os lados, primeiro o esquerdo.
 * Pernas sozinhas numa escada (ou fora de escadas) ficam com `null`.
 */
export function pernasNaEscada(cena: CenaTabletop, refs: ReadonlyArray<ReferenciaPonto | null>): ReadonlyArray<PernaNaEscada | null> {
  const resultado: Array<PernaNaEscada | null> = refs.map(() => null)
  const porEscada = new Map<string, number[]>()
  refs.forEach((r, i) => {
    if (r && cena.objetos.find((o) => o.id === r.idObjeto)?.tipo === 'escada') porEscada.set(r.idObjeto, [...(porEscada.get(r.idObjeto) ?? []), i])
  })
  for (const [idEscada, indices] of porEscada) {
    if (indices.length < 2) continue
    const pontos = pontosDeAncoragem(cena.objetos.find((o) => o.id === idEscada)!)
    const pe = pontos.find((p) => p.id === 'pe')!.local
    const eixo = unit(sub(pontos.find((p) => p.id === 'topo')!.local, pe))
    const locais = indices.map((i) => pontos.find((p) => p.id === refs[i]!.idPonto)?.local ?? null)
    const altura = (l: V | null) => (l ? (l[0] - pe[0]) * eixo[0] + (l[1] - pe[1]) * eixo[1] + (l[2] - pe[2]) * eixo[2] : -Infinity)
    const nivelLocal = locais.reduce<V | null>((maior, l) => (l && (!maior || altura(l) > altura(maior)) ? l : maior), null)
    if (!nivelLocal) continue
    const ladoFixo = (i: number): LadoDoBanzo | null =>
      refs[i]!.idPonto.startsWith('banzo-esq-') ? 'esq' : refs[i]!.idPonto.startsWith('banzo-dir-') ? 'dir' : null
    const usados = { esq: 0, dir: 0 }
    for (const i of indices) {
      const l = ladoFixo(i)
      if (l) usados[l] += 1
    }
    for (const i of indices) {
      let lado = ladoFixo(i)
      if (!lado) {
        lado = usados.esq <= usados.dir ? 'esq' : 'dir'
        usados[lado] += 1
      }
      resultado[i] = { nivelLocal, lado }
    }
  }
  return resultado
}

/** O lado do banzo de cada perna no empate: a primeira à esquerda, a segunda à direita, e assim por diante. */
export const ladoDaPerna = (indice: number): LadoDoBanzo => (indice % 2 === 0 ? 'esq' : 'dir')

/**
 * Quanto cada fio do anel dobrado alcança, sem carga, para cobrir `distancia`
 * metros (do banzo ao mosquetão): o menor anel comercial que sobra pelo menos
 * um pouco, sem passar de uma sobra exagerada.
 */
export function comprimentoDoFioSemCarga(distancia: number, raioDoApoio = 0): number {
  if (!(distancia > 0)) return 0
  // a volta no apoio consome meia circunferência do anel
  const volta = Math.PI * raioDoApoio
  const minimo = distancia * (1 + SOBRA_MINIMA)
  const comercial = COMPRIMENTOS_ANEL_DE_FITA_M.map((c) => c / 2 - volta / 2).find((alcance) => alcance >= minimo)
  return Math.min(comercial ?? minimo, distancia * (1 + SOBRA_MAXIMA))
}

/** O anel comercial (volta inteira, em metros) usado para uma distância; `null` se nenhum alcança. */
export function anelComercialPara(distancia: number, raioDoApoio = 0): number | null {
  const volta = Math.PI * raioDoApoio
  return COMPRIMENTOS_ANEL_DE_FITA_M.find((c) => c / 2 - volta / 2 >= distancia * (1 + SOBRA_MINIMA)) ?? null
}

const folga = (corda: CordaTabletop, i: number) => corda.folgas?.[i] ?? 0

/**
 * A peça tem peso: alguma corda (fora `exceto`) passa por ela ou termina nela
 * com o trecho que chega ali esticado (sem folga).
 */
export function pecaSobCarga(cena: CenaTabletop, idObjeto: string, exceto?: string): boolean {
  for (const corda of cena.cordas ?? []) {
    if (corda.id === exceto) continue
    const n = corda.percurso.length
    for (let k = 0; k < n; k += 1) {
      if (corda.percurso[k]!.idObjeto !== idObjeto) continue
      const antes = k > 0 && folga(corda, k - 1) === 0
      const depois = k < n - 1 && folga(corda, k) === 0
      if (antes || depois) return true
    }
  }
  return false
}

/** Um trecho de uma corda-fita: a ponta que abraça a estrutura e a que termina na peça. */
/**
 * Peças em que a fita não entra direto: ela clipa num mosquetão, e o
 * mosquetão é que passa pelo olhal (da polia, da placa, do sistema...).
 */
const PECAS_COM_MOSQUETAO_NO_OLHAL: ReadonlySet<ObjetoTabletop['tipo']> = new Set([
  'polia',
  'poliaDupla',
  'bloqueador',
  'descensor',
  'sistemaVM',
  'placaAncoragem',
  'pontoAncoragem',
  'maca',
])

/** A fita que termina nesta peça precisa de um mosquetão no olhal. */
export const fitaPedeMosquetao = (objeto: Pick<ObjetoTabletop, 'tipo' | 'parametros'> | undefined): boolean =>
  // a alça de fita é o próprio laço da fita: ela termina nele, sem mosquetão automático (o usuário clipa o que quiser)
  Boolean(objeto && PECAS_COM_MOSQUETAO_NO_OLHAL.has(objeto.tipo) && !(objeto.tipo === 'pontoAncoragem' && objeto.parametros?.alcaFita === 1))

/** Metade do comprimento do mosquetão da cena: do olhal ao meio dele, e do meio ao topo. */
export const MEIO_MOSQUETAO_M = 0.05

/** O mosquetão que une as fitas ao olhal de uma peça. */
export interface MosquetaoDaFita {
  /** O meio do mosquetão. */
  readonly centro: V
  /** O eixo maior do mosquetão, do olhal para as fitas, unitário. */
  readonly eixo: V
  /** Onde as fitas passam: a ponta do mosquetão do lado oposto ao olhal. */
  readonly topo: V
}

/**
 * O mosquetão clipado no `olhal`: ele continua o eixo do olhal (o +Y da peça,
 * para onde o olhal aponta), que é como a peça pende dele. Se esse eixo não
 * aponta para as fitas que sobem a `apoios` (a peça está virada), o mosquetão
 * segue a soma das direções das fitas; sem nada disso, fica em pé.
 */
export function mosquetaoNoOlhal(olhal: V, apoios: readonly V[], fator = 1, eixoDoOlhal: V | null = null): MosquetaoDaFita {
  const soma = apoios.reduce<V>((s, a) => somaL(s, unit(sub(a, olhal))), [0, 0, 0])
  const rumo: V | null = Math.hypot(soma[0], soma[1], soma[2]) > 1e-6 ? unit(soma) : null
  const doOlhal = eixoDoOlhal && Math.hypot(...eixoDoOlhal) > 1e-6 ? unit(eixoDoOlhal) : null
  const eixo: V = doOlhal && (!rumo || doOlhal[0] * rumo[0] + doOlhal[1] * rumo[1] + doOlhal[2] * rumo[2] > 0) ? doOlhal : (rumo ?? [0, 1, 0])
  const meio = MEIO_MOSQUETAO_M * fator
  return { centro: somaL(olhal, esc(eixo, meio)), eixo, topo: somaL(olhal, esc(eixo, 2 * meio)) }
}

/** Onde as fitas terminam numa peça e, se for o caso, o mosquetão que as une ao olhal dela. */
export interface PontaDaFita {
  readonly ponta: V
  /** O mosquetão a desenhar (a peça tem olhal e não é um mosquetão). */
  readonly mosquetao?: MosquetaoDaFita
  /** A barra do mosquetão que o laço da fita abraça. */
  readonly barra?: BarraDoMosquetao
}

/** A barra do mosquetão por onde passa o laço da fita. */
export interface BarraDoMosquetao {
  /** O centro da barra (no eixo do tubo de metal). */
  readonly centro: V
  /** A direção da barra, unitária: a fita deita ao longo dela. */
  readonly eixo: V
  /** O raio do tubo de metal. */
  readonly raio: number
}

/**
 * O mosquetão da cena (ver `MosquetaoTabletop`), no referencial dele: o eixo
 * maior em +Y, as duas barras curtas atravessando em X, a 5 cm do centro,
 * e o tubo de 5,5 mm de raio.
 */
const BARRA_DE_CIMA_LOCAL: V = [-0.003, 0.05, 0]
const BARRA_DE_BAIXO_LOCAL: V = [-0.004, -0.05, 0]
export const RAIO_BARRA_MOSQUETAO_M = 0.0055

/** O quatérnio (x, y, z, w) que leva +Y a `t` (unitário), como o `setFromUnitVectors` do Three.js. */
function quaternioDeY(t: V): readonly [number, number, number, number] {
  const r = t[1] + 1
  const q: [number, number, number, number] = r < 1e-6 ? [0, 0, 1, 0] : [t[2], 0, -t[0], r]
  const n = Math.hypot(...q)
  return [q[0] / n, q[1] / n, q[2] / n, q[3] / n]
}

/** Gira `v` pelo quatérnio `q`. */
function girar(q: readonly [number, number, number, number], v: V): V {
  const u: V = [q[0], q[1], q[2]]
  const w = q[3]
  const uv: V = [u[1] * v[2] - u[2] * v[1], u[2] * v[0] - u[0] * v[2], u[0] * v[1] - u[1] * v[0]]
  const uuv: V = [u[1] * uv[2] - u[2] * uv[1], u[2] * uv[0] - u[0] * uv[2], u[0] * uv[1] - u[1] * uv[0]]
  return [v[0] + 2 * (w * uv[0] + uuv[0]), v[1] + 2 * (w * uv[1] + uuv[1]), v[2] + 2 * (w * uv[2] + uuv[2])]
}

/**
 * A barra de um mosquetão desenhado com o eixo maior em `eixo` (com o mesmo
 * giro do desenho), no `centro`: a de cima (`lado` 1, para onde o eixo
 * aponta) ou a de baixo (−1). `noMundo` leva uma direção ou um ponto do
 * referencial do grupo do mosquetão para o mundo (a peça girada); sem ele, o
 * grupo não tem giro.
 */
export function barraDoMosquetao(
  centro: V,
  eixo: V,
  lado: 1 | -1,
  fator = 1,
  noMundo: { ponto: (l: V) => V; direcao: (l: V) => V } | null = null,
): BarraDoMosquetao {
  const q = quaternioDeY(unit(eixo))
  const local = esc(girar(q, lado === 1 ? BARRA_DE_CIMA_LOCAL : BARRA_DE_BAIXO_LOCAL), fator)
  const direcao = girar(q, [1, 0, 0])
  return {
    centro: noMundo ? noMundo.ponto(local) : somaL(centro, local),
    eixo: unit(noMundo ? noMundo.direcao(direcao) : direcao),
    raio: RAIO_BARRA_MOSQUETAO_M * fator,
  }
}

/** A mesma barra, deslocada `deslocamento` metros ao longo dela: cada fita no seu lugar da barra. */
export const naBarra = (barra: BarraDoMosquetao, deslocamento: number): BarraDoMosquetao => ({
  ...barra,
  centro: somaL(barra.centro, esc(barra.eixo, deslocamento)),
})

/** Quantos metros de barra cada fita ocupa (a fita se franze no mosquetão). */
export const FITA_NA_BARRA_M = 0.0095

/** O deslocamento da fita `i` de `n` na barra, para ficarem lado a lado e centradas. */
export const lugarNaBarra = (i: number, n: number, fator = 1): number => (i - (n - 1) / 2) * FITA_NA_BARRA_M * fator

/**
 * A barra de um mosquetão da cena (peça do tipo `mosquetao`) voltada para os
 * pontos `rumos` (as fitas que sobem dele, as cordas que saem dele). O
 * desenho gira o mosquetão para o eixo da carga (`orientacaoDoMosquetao`);
 * sem carga, ele fica em pé no referencial da peça.
 */
export function barraDoMosquetaoParaOs(cena: CenaTabletop, objeto: ObjetoTabletop, rumos: readonly V[], fator = 1): BarraDoMosquetao {
  const t = objeto.transformacao
  const passagem = pontosDeAncoragem(objeto).find((p) => p.id === 'passagem')?.local ?? [0, 0, 0]
  const ponto = localParaMundo(esc(passagem, fator), t)
  const direcaoNoMundo = (l: V) => unit(sub(localParaMundo(l, t), localParaMundo([0, 0, 0], t)))
  const { eixo } = orientacaoDoMosquetao(cena, objeto)
  const e = eixo ?? direcaoNoMundo([0, 1, 0])
  const soma = rumos.reduce<V>((s, a) => somaL(s, unit(sub(a, ponto))), [0, 0, 0])
  const lado = e[0] * soma[0] + e[1] * soma[1] + e[2] * soma[2] >= 0 ? 1 : -1
  // o eixo no referencial da peça, como o desenho o recebe
  const eLocal = unit(sub(mundoParaLocalDoObjeto(somaL(t.posicao as V, e), t), mundoParaLocalDoObjeto(t.posicao as V, t)))
  return barraDoMosquetao(ponto, eLocal, lado, fator, {
    ponto: (l) => localParaMundo(somaL(esc(passagem, fator), l), t),
    direcao: direcaoNoMundo,
  })
}

/**
 * Onde as fitas que sobem para `apoios` terminam na peça da `referencia`:
 *
 *  - num mosquetão, a fita abraça a barra dele voltada para as fitas (não
 *    passa pelo meio dele); o eixo é o da carga;
 *  - numa peça com olhal (polia, placa...), num mosquetão clipado no olhal,
 *    que continua o eixo do olhal: as fitas abraçam a barra de cima dele;
 *  - nas demais, no próprio ponto.
 */
export function pontaDaFitaNaPeca(cena: CenaTabletop, referencia: ReferenciaPonto, apoios: readonly V[], fator = 1): PontaDaFita | null {
  const objeto = cena.objetos.find((o) => o.id === referencia.idObjeto)
  const achado = objeto && pontosDeAncoragem(objeto).find((p) => p.id === referencia.idPonto)
  if (!objeto || !achado) return null
  const mundo = (local: V) => localParaMundo(esc(local, fator), objeto.transformacao)
  const ponto = mundo(achado.local)
  if (objeto.tipo === 'mosquetao') {
    const barra = barraDoMosquetaoParaOs(cena, objeto, apoios, fator)
    return { ponta: barra.centro, barra }
  }
  if (fitaPedeMosquetao(objeto)) {
    // o +Y da peça no mundo: para onde aponta o olhal
    const eixoDoOlhal = unit(sub(localParaMundo([0, 1, 0], objeto.transformacao), localParaMundo([0, 0, 0], objeto.transformacao)))
    const mosquetao = mosquetaoNoOlhal(ponto, apoios, fator, eixoDoOlhal)
    const barra = barraDoMosquetao(mosquetao.centro, mosquetao.eixo, 1, fator)
    return { ponta: barra.centro, mosquetao, barra }
  }
  return { ponta: ponto }
}

export interface TrechoDeFita {
  readonly apoio: ApoioDaFita
  /** Onde a fita termina (o mosquetão, o olhal). */
  readonly ponta: V
  /** Comprimento de cada fio do anel sem carga. */
  readonly comprimentoSemCargaM: number
  readonly carregada: boolean
  /**
   * A fita termina numa peça com olhal (polia, placa...): o mosquetão que une
   * a fita ao olhal. Vem só no primeiro trecho que chega a cada olhal, para se
   * desenhar uma vez; os demais terminam no mesmo `ponta`.
   */
  readonly mosquetao?: MosquetaoDaFita
  /** A barra do mosquetão que o laço abraça, já no lugar desta fita (lado a lado com as outras). */
  readonly barra?: BarraDoMosquetao
}

/**
 * Uma corda de função `fita` desenhada como anéis de fita, um por trecho: a
 * ponta que está numa estrutura (escada, toco) é a que o anel abraça; a outra
 * termina na peça (mosquetão), ou num mosquetão clipado no olhal da peça
 * (polia, placa...). Na escada, a primeira ponta vai para o banzo esquerdo e
 * a última para o direito quando empata.
 */
export function trechosDaFita(
  cena: CenaTabletop,
  corda: CordaTabletop,
  fatorDaPeca: (idObjeto: string) => number = () => 1,
): ReadonlyArray<TrechoDeFita | null> {
  const refs = corda.percurso
  const objetoDe = (r: ReferenciaPonto) => cena.objetos.find((o) => o.id === r.idObjeto)
  const centro = (r: ReferenciaPonto): V | null => {
    const o = objetoDe(r)
    const p = o && pontosDeAncoragem(o).find((x) => x.id === r.idPonto)
    return o && p ? localParaMundo(esc(p.local, fatorDaPeca(o.id)), o.transformacao) : null
  }
  type Bruto = { apoio: ApoioDaFita; olhal: V; refPonta: ReferenciaPonto; sobra: number }
  // de cada trecho, a ponta que abraça a estrutura (se as duas forem, a de trás) e a que termina na peça
  const pares = refs.slice(0, -1).map((a, i) => {
    const b = refs[i + 1]!
    const oa = objetoDe(a)
    const ob = objetoDe(b)
    if (!oa || !ob) return null
    const abracaB = !fitaAbraca(oa) && fitaAbraca(ob)
    return abracaB ? { refApoio: b, refPonta: a, abracaB } : { refApoio: a, refPonta: b, abracaB }
  })
  // as pontas na mesma escada ficam na mesma altura, uma em cada banzo
  const naEscada = pernasNaEscada(cena, pares.map((x) => x?.refApoio ?? null))
  const brutos: Array<Bruto | null> = pares.map((par, i) => {
    if (!par) return null
    const olhal = centro(par.refPonta)
    const lado: LadoDoBanzo = par.abracaB && i + 1 === refs.length - 1 && i > 0 ? 'dir' : 'esq'
    const apoio = olhal && apoioDaFita(cena, par.refApoio, olhal, lado, fatorDaPeca(par.refApoio.idObjeto), naEscada[i])
    return olhal && apoio ? { apoio, olhal, refPonta: par.refPonta, sobra: folga(corda, i) } : null
  })

  // as fitas que chegam na mesma peça terminam no mesmo lugar (e no mesmo mosquetão)
  const chave = (r: ReferenciaPonto) => `${r.idObjeto}.${r.idPonto}`
  const pontas = new Map<string, PontaDaFita | null>()
  for (const b of brutos) {
    if (!b || pontas.has(chave(b.refPonta))) continue
    const apoios = brutos.filter((x): x is Bruto => x !== null && chave(x.refPonta) === chave(b.refPonta)).map((x) => x.apoio.ponto)
    pontas.set(chave(b.refPonta), pontaDaFitaNaPeca(cena, b.refPonta, apoios, fatorDaPeca(b.refPonta.idObjeto)))
  }

  const desenhados = new Set<string>()
  // as fitas que chegam na mesma barra ficam lado a lado nela
  const quantas = new Map<string, number>()
  for (const b of brutos) if (b) quantas.set(chave(b.refPonta), (quantas.get(chave(b.refPonta)) ?? 0) + 1)
  const ja = new Map<string, number>()
  return brutos.map((b) => {
    if (!b) return null
    const k = chave(b.refPonta)
    const fim = pontas.get(k)
    const mosquetao = fim?.mosquetao
    const ordem = ja.get(k) ?? 0
    ja.set(k, ordem + 1)
    const barra = fim?.barra ? naBarra(fim.barra, lugarNaBarra(ordem, quantas.get(k)!, fatorDaPeca(b.refPonta.idObjeto))) : undefined
    const ponta = barra?.centro ?? fim?.ponta ?? b.olhal
    const distancia = dist(b.apoio.ponto, ponta)
    const primeiro = mosquetao && !desenhados.has(k)
    if (primeiro) desenhados.add(k)
    return {
      apoio: b.apoio,
      ponta,
      comprimentoSemCargaM: Math.max(comprimentoDoFioSemCarga(distancia, b.apoio.raio), distancia + b.sobra),
      carregada: b.sobra === 0 && pecaSobCarga(cena, b.refPonta.idObjeto, corda.id),
      ...(barra ? { barra } : {}),
      ...(primeiro ? { mosquetao } : {}),
    }
  })
}
